<template>
    <common-dropdown :isOpen="isOpen" arrowPositionY="top" arrowPositionX="left">
        <section class="appointment-calendar-slots">
            <common-loader :isProcessing="isSending"></common-loader>
            <schedule-24-warning-modal
                v-if="is24WarningOpen"
                :physician="physician"
                :practice="practice"
                :location="location"
                @close="close24Warning()"
                @continue="confirmSlot()"
            ></schedule-24-warning-modal>

            <template v-if="isSent">
                <header class="appointment-calendar-slots__header">
                    <common-date
                        :date="date"
                        :copy="$tc('pages.referrals.new.steps.schedule.slots_available', availableSlotsCount)"
                    ></common-date>
                </header>

                <div
                    v-if="availableSlotsCount > 0"
                    ref="options"
                    class="appointment-calendar-slots__content"
                    :class="{ 'appointment-calendar-slots__content--pool': isReferralPool }"
                >
                    <section v-if="amOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.am') }}
                        </h5>

                        <common-content-spacer v-if="isReferralPool" height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in amOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>

                        <radio-input
                            v-else
                            class="appointment-calendar-slots__group--radio"
                            :value="selectedSlot"
                            :options="amOptions"
                            direction="column"
                            checkedIcon="radio-check"
                            @change="selectSlot($event)"
                        ></radio-input>
                    </section>

                    <section v-if="pmOptions.length" class="appointment-calendar-slots__group">
                        <h5 class="appointment-calendar-slots__time-of-day">
                            {{ $t('pages.referrals.new.steps.schedule.pm') }}
                        </h5>

                        <common-content-spacer v-if="isReferralPool" height="tiny">
                            <checkbox-input
                                v-for="(slot, index) in pmOptions"
                                :key="index"
                                :isDisabled="
                                    totalSlotCount > 2 && !selectedSlots.some((selectedSlot) => isequal(selectedSlot, slot))
                                "
                                :label="slot.label"
                                :value="checked(slot)"
                                @change="selectMultipleSlots(slot)"
                            ></checkbox-input>
                        </common-content-spacer>

                        <radio-input
                            v-else
                            :value="selectedSlot"
                            :options="pmOptions"
                            direction="column"
                            checkedIcon="radio-check"
                            @change="selectSlot($event)"
                        ></radio-input>
                    </section>
                </div>
                <div
                    v-else
                    ref="options"
                    class="appointment-calendar-slots__content"
                    :class="{ 'appointment-calendar-slots__content--pool': isReferralPool }"
                >
                    <section class="appointment-calendar-slots__group">
                        <h1>{{ $t('pages.referrals.new.steps.schedule.not_available') }}</h1>
                    </section>
                </div>

                <footer v-if="!isReferralPool" class="appointment-calendar-slots__footer">
                    <template v-if="selectedSlot">
                        <div class="appointment-calendar-slots__details">
                            {{ mapSlotToOption(selectedSlot).label }}
                        </div>
                    </template>

                    <div class="appointment-calendar-slots__submit">
                        <common-button
                            :label="$t(`pages.referrals.new.steps.schedule.${buttonType}`)"
                            :isDisabled="!selectedSlot"
                            :isProcessing="isProcessing"
                            type="primary"
                            size="large"
                            @click="handleClick"
                        ></common-button>
                    </div>
                </footer>
            </template>
        </section>
    </common-dropdown>
</template>

<script>
    import dayjs from 'dayjs'
    import UsersAPI from 'api/UsersAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import CalendarSlotsMixin from 'mixins/CalendarSlotsMixin'
    import RadioInput from 'components/form-inputs/RadioInput'
    import CheckboxInput from 'components/form-inputs/CheckboxInput'
    import Schedule24WarningModal from 'components/page-elements/referrals-new-schedule/Schedule24WarningModal'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import isequal from 'lodash.isequal'
    import emitter from 'tiny-emitter/instance'
    import DemoReferral from 'services/DemoReferral'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PatientAPI from 'api/PatientAPI'
    import DiagnosisAPI from 'api/DiagnosisAPI'
    import FormMixin from 'mixins/FormMixin'

    export default {
        mixins: [CalendarSlotsMixin, FormMixin],

        components: {
            RadioInput,
            CheckboxInput,
            Schedule24WarningModal,
        },

        props: {
            physician: { type: Object, required: true },
            practice: { type: Object, required: true },
            location: { type: Object, required: true },
            selectedSlotsByDate: { type: Object, required: false, default: undefined },
        },

        inject: {
            isReferralPool: {
                default: false,
            },
            sharedState: {},
        },

        data() {
            return {
                slots: [],
                selectedSlot: null,
                selectedSlots: [],
                is24WarningOpen: false,
                isequal: isequal,
                data: {},
                isProcessing: false,
            }
        },

        computed: {
            availableSlotsCount() {
                return this.slots.filter(this.filterFutureSlots).length
            },

            buttonType() {
                if (this.$route.params.action === 'update-slot') {
                    return 'confirm_slot'
                } else if (this.$route.query.referralId) {
                    return 'select_slot'
                } else {
                    return 'request_slot'
                }
            },

            totalSlotCount() {
                return this.sharedState.totalSlotCount
            },

            amOptions() {
                return this.slots
                    .filter(({ to }) => to <= 12 * 60)
                    .filter(this.filterFutureSlots)
                    .map(this.mapSlotToOption)
            },

            pmOptions() {
                return this.slots
                    .filter(({ from }) => from >= 12 * 60)
                    .filter(this.filterFutureSlots)
                    .map(this.mapSlotToOption)
            },
        },

        methods: {
            filterFutureSlots(slot) {
                const slotDateTime = this.date.clone().add(slot.from, 'minutes')
                if (slotDateTime.isBefore(dayjs().add(1, 'day')) && slotDateTime.isBefore(dayjs())) {
                    return false
                }

                return true
            },

            selectSlot(slot) {
                if (slot && this.date.add(slot.from, 'minutes').isBefore(dayjs().add(1, 'day'))) {
                    this.is24WarningOpen = true
                    this.selectedSlot = slot
                    return
                }
                this.selectedSlot = slot
            },
            selectMultipleSlots(slot) {
                if (slot && this.date.add(slot.value.from, 'minutes').isBefore(dayjs().add(1, 'day'))) {
                    return (this.is24WarningOpen = true)
                }

                const index = this.selectedSlots.findIndex((s) => isequal(s, slot))
                if (index !== -1) {
                    this.selectedSlots.splice(index, 1)
                } else {
                    this.selectedSlots.push(slot)
                }
                this.emitEvent({
                    name: 'selectSlot',
                    payload: {
                        slots: this.selectedSlots,
                        date: this.date,
                    },
                })
            },
            checked(slot) {
                return this.selectedSlots.some((s) => isequal(s, slot))
            },

            close24Warning() {
                this.is24WarningOpen = false
            },

            confirmSlot() {
                this.emitEvent({
                    name: 'selectSlot',
                    payload: {
                        slot: this.selectedSlot,
                        date: this.date,
                    },
                })
            },

            async selectSlotForReschedule() {
                this.isProcessing = true
                await this.loadData()
                ReferralsAPI.create(this.data).then(() => this.emitSent(this.data))
            },

            async handleClick() {
                if (this.$route.query.referralId) {
                    await this.selectSlotForReschedule()
                } else {
                    this.confirmSlot()
                }
            },

            mapSlotToOption(slot) {
                let start = dayjs().startOf('day').add(slot.from, 'minutes').format('H:mm')
                let end = dayjs().startOf('day').add(slot.to, 'minutes').format('H:mm')
                if (start.length < 5) {
                    start = '0' + start
                }
                if (end.length < 5) {
                    end = '0' + end
                }
                return {
                    label: [start, end].join(' – '),
                    value: slot,
                }
            },

            async loadData() {
                this.data.fromUserId = parseInt(this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id)
                this.data.toUserId = parseInt(this.$route.query.physicianId)
                this.data.insurancePlanId = parseInt(this.$route.query.insurancePlanId)
                this.data.subSpecialityId = parseInt(this.$route.query.subSpecialityId)
                this.data.date = this.selectedSlot?.date
                this.data.startTime = this.selectedSlot?.startTime
                this.data.toPracticeId = parseInt(this.$route.query.practiceId)
                this.data.toPracticeLocationId = parseInt(this.$route.query.practiceLocationId)
                this.data.isInsuranceAuthorizationRequired = false
                this.data.isPrimaryCareOutsideMedmatch = false
                this.data.patient = {}

                if (this.$route?.query?.patientId) {
                    try {
                        const [patientRes, referralRes] = await Promise.all([
                            PatientAPI.getPatientDetails(this.$route.query.patientId, this.$auth.getUser().id),
                            ReferralsAPI.get(this.$route?.query?.referralId, this.$auth.getActiveMember().id),
                        ])

                        this.patientDetails = patientRes
                        this.data.insuranceGroupNumber = patientRes?.mcard?.groupNumber ?? null
                        this.data.insuranceNumber = patientRes?.mcard?.memberId ?? null
                        this.data.patient = {
                            firstName: patientRes?.firstName,
                            lastName: patientRes?.lastName,
                            dateOfBirth: patientRes?.dateOfBirth,
                            mobilePhone: patientRes?.mobilePhone,
                            address: patientRes?.address,
                            city: patientRes?.city,
                            state: patientRes?.state,
                            zip: patientRes?.zip,
                            floorNumber: patientRes?.floorNumber,
                            apartmentNumber: patientRes?.apartmentNumber,
                            buildingNumber: patientRes?.buildingNumber,
                            ssn: patientRes?.ssn,
                        }

                        this.activeReferral = referralRes
                        if (this.activeReferral?.diagnosis) {
                            const diagnosisText = this.activeReferral.diagnosis
                            this.data.diagnosis = diagnosisText

                            const icdResponse = await DiagnosisAPI.getFromICD(diagnosisText, 'name')
                            if (icdResponse) {
                                this.data.icdCodeId = icdResponse[0].data.id
                            }
                        }
                    } catch (err) {
                        this.isProcessing = false
                        return
                    }
                }
                if (this.$route.query.followUpVisit) {
                    this.data.followUpReferral = Number(this.$route.query.referralId)
                }
            },
        },

        watch: {
            isOpen() {
                if (!this.isOpen || this.slots.length) {
                    return
                }

                const request = {
                    year: this.date.format('YYYY'),
                    month: this.date.format('M'),
                    day: this.date.format('D'),
                    practiceId: this.isReferralPool ? this.$route.query.practiceId : this.practice?.id,
                    practiceLocationId: this.isReferralPool ? this.$route.query.practiceLocationId : this.location.id,
                }

                if (this.$auth.isUserPracticeDemo()) {
                    this.slots = DemoReferral.getSlots().map((slot) => ({
                        ...slot,
                        date: dayjs(this.date).format('YYYY-MM-DD'),
                    }))
                    this.isSending = false
                    this.isSent = true
                    this.$validation.clear()
                    this.indicateScrollability()
                    return false
                }

                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    this.handleRequest(PracticesAPI.getAvailableSlots(this.$route.query.practiceId, request)).then((slots) => {
                        this.slots = slots
                        this.indicateScrollability()
                        this.isSent = true
                        this.isSending = false
                    })
                } else {
                    const userId = this.isReferralPool
                        ? this.$auth.getActiveMember()?.id
                            ? this.$auth.getActiveMember()?.id
                            : this.$auth.getUser().id
                        : this.physician.id
                    this.handleRequest(UsersAPI.getAvailableSlots(userId, request)).then((slots) => {
                        this.slots = slots
                        this.indicateScrollability()
                        this.isSent = true
                        this.isSending = false
                        if (this.isReferralPool) {
                            const formattedDate = dayjs(this.date).format('YYYY-MM-DD')

                            let foundSlots = []

                            Object.values(this.selectedSlotsByDate).forEach((slots) => {
                                const filteredSlots = slots.filter((slot) => slot.date === formattedDate)
                                foundSlots.push(...filteredSlots)
                            })

                            if (foundSlots.length > 0) {
                                this.selectedSlots = foundSlots.map((slot) => {
                                    return {
                                        label: slot.label,
                                        value: {
                                            date: slot.date,
                                            endTime: slot.endTime,
                                            from: slot.from,
                                            startTime: slot.startTime,
                                            to: slot.to,
                                        },
                                    }
                                })
                            }
                        }
                    })
                }
            },
        },

        mounted() {
            emitter.on('slot-removed', (payload) => {
                if (dayjs(this.date).utc().format() == dayjs(payload.date).utc().format()) {
                    const slot = {
                        label: payload.slot.label,
                        value: {
                            date: payload.slot.date,
                            endTime: payload.slot.endTime,
                            from: payload.slot.from,
                            startTime: payload.slot.startTime,
                            to: payload.slot.to,
                        },
                    }
                    slot.value
                    this.selectMultipleSlots(slot)
                }
            })
        },

        beforeDestroy() {
            emitter.off('slot-removed')
        },
    }
</script>
